import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface Crumb {
  label: string;
  to?: string;
  search?: Record<string, string>;
}

export function PageHeader({
  title,
  description,
  actions,
  crumbs,
  meta,
  children,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  crumbs?: Crumb[];
  meta?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn("border-b bg-background px-5 pt-3 pb-2.5", className)}>
      {crumbs?.length ? (
        <nav aria-label="Breadcrumb" className="mb-1 flex items-center gap-1 text-muted-foreground text-xs">
          {crumbs.map((c, i) => (
            <span key={`${c.to ?? ""}:${c.label}`} className="flex items-center gap-1">
              {c.to ? (
                // biome-ignore lint/suspicious/noExplicitAny: crumbs point at static routes
                <Link to={c.to as any} search={c.search as any} className="hover:text-foreground">
                  {c.label}
                </Link>
              ) : (
                <span>{c.label}</span>
              )}
              {i < crumbs.length - 1 ? <ChevronRight className="size-3" aria-hidden /> : null}
            </span>
          ))}
        </nav>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-semibold text-base tracking-tight">{title}</h1>
          {description ? <p className="text-muted-foreground text-xs">{description}</p> : null}
          {meta ? <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground text-xs">{meta}</div> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {children ? <div className="mt-2.5">{children}</div> : null}
    </header>
  );
}

export function PageBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("flex min-h-0 flex-1 flex-col gap-4 p-5", className)}>{children}</div>;
}
